import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    ArrowLeft, Package, User, Calendar, CreditCard,
    Clock, FileText, Truck, Hash, Printer, Save, X, Edit2, Bell, Mail
} from 'lucide-react';
import { ordersAPI, customersAPI, notificationsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LabelPrint from '../../components/LabelPrint';
import PaymentModal from '../../components/PaymentModal';
import SendNotificationModal from '../../components/SendNotificationModal';

interface CustomerInfo {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    postcode?: string;
}

interface OrderItem {
    _id: string;
    serviceId: { _id: string; name: string; code: string; category: string; pricingType: string; weightUnit: string } | null;
    quantity: number;
    weight?: number;
    unitPrice: number;
    netPrice: number;
    vatRate: number;
    vatAmount: number;
    totalPrice: number;
    isExpress: boolean;
    expressSurcharge: number;
    discountAmount: number;
    specialInstructions?: string;
    status: string;
}

interface OrderData {
    _id: string;
    orderNumber: string;
    customerId: CustomerInfo | string | null;
    createdById?: { firstName: string; lastName: string } | null;
    status: string;
    priority: string;
    orderType: string;
    subtotal: number;
    vatAmount: number;
    totalAmount: number;
    discountAmount: number;
    paidAmount: number;
    balanceAmount: number;
    paymentStatus: string;
    paymentMethod?: string;
    specialInstructions?: string;
    customerNotes?: string;
    internalNotes?: string;
    dueDate?: string;
    completedDate?: string;
    collectedDate?: string;
    isDelivery: boolean;
    deliveryAddress?: string;
    deliveryPostcode?: string;
    deliveryInstructions?: string;
    itemCount: number;
    totalWeight: number;
    loyaltyPointsEarned?: number;
    loyaltyPointsRedeemed?: number;
    appliedDiscount?: { code?: string; name?: string };
    items: OrderItem[];
    createdAt: string;
    updatedAt: string;
}

const statusColors: Record<string, string> = {
    received: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    ready: 'bg-purple-100 text-purple-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
};

const AVAILABLE_STATUSES = [
    { value: 'received', label: 'Received' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'ready', label: 'Ready for Collection/Delivery' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
];

const paymentStatusColors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-800',
    partial: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    refunded: 'bg-red-100 text-red-800',
};

const priorityColors: Record<string, string> = {
    standard: 'bg-gray-100 text-gray-700',
    express: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700',
};

const OrderDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [order, setOrder] = useState<OrderData | null>(null);
    const [customerData, setCustomerData] = useState<CustomerInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [showLabel, setShowLabel] = useState<'garment' | 'receipt' | false>(false);

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [emailSending, setEmailSending] = useState(false);

    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [editingStatus, setEditingStatus] = useState(false);
    const [tempStatus, setTempStatus] = useState('');

    const [isUpdatingDueDate, setIsUpdatingDueDate] = useState(false);
    const [editingDueDate, setEditingDueDate] = useState(false);
    const [tempDueDate, setTempDueDate] = useState('');

    const fetchOrder = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const res = await ordersAPI.getOrder(id);
            const orderData = res.data;
            setOrder(orderData);

            // If customerId is populated object, use it directly
            if (orderData.customerId && typeof orderData.customerId === 'object') {
                setCustomerData(orderData.customerId as CustomerInfo);
            } else if (orderData.customerId && typeof orderData.customerId === 'string') {
                // Fetch customer separately
                try {
                    const custRes = await customersAPI.getCustomer(orderData.customerId);
                    setCustomerData(custRes.data);
                } catch { /* customer not found */ }
            }
        } catch {
            toast.error('Failed to load order');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrder();
    }, [id]);

    const handleEmailReceipt = async () => {
        if (!id) return;
        setEmailSending(true);
        try {
            const res = await notificationsAPI.emailReceipt(id);
            toast.success(res.data.message || 'Receipt emailed successfully');
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Failed to email receipt');
        } finally {
            setEmailSending(false);
        }
    };

    const handlePaymentComplete = async (paymentMethod: string) => {
        setShowPaymentModal(false);
        // Refresh the order to get updated balance/payment status
        await fetchOrder();
        // The toast success is already handled inside the PaymentModal
    };

    const handleUpdateStatus = async () => {
        if (!id || !tempStatus) return;
        setIsUpdatingStatus(true);
        try {
            await ordersAPI.updateOrderStatus(id, tempStatus);
            toast.success('Order status updated');
            setEditingStatus(false);
            fetchOrder();
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to update status');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleUpdateDueDate = async () => {
        if (!id || !tempDueDate) return;
        setIsUpdatingDueDate(true);
        try {
            await ordersAPI.updateOrderDueDate(id, tempDueDate);
            toast.success('Due date updated');
            setEditingDueDate(false);
            fetchOrder();
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to update due date');
        } finally {
            setIsUpdatingDueDate(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
        );
    }

    if (!order) return null;

    // Use the separately fetched customer data
    const customerId = customerData?._id || (typeof order.customerId === 'string' ? order.customerId : (order.customerId as any)?._id) || null;
    const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
    const formatDateTime = (d?: string) => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

    return (
        <>
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-6xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-6">
                        <button onClick={() => navigate(-1)} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Back
                        </button>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-primary-100 flex items-center justify-center">
                                    <Package className="h-6 w-6 text-primary-600" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">{order.orderNumber}</h1>
                                    <p className="text-sm text-gray-500">Created {formatDateTime(order.createdAt)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {editingStatus ? (
                                    <div className="flex items-center gap-1">
                                        <select
                                            value={tempStatus}
                                            onChange={(e) => setTempStatus(e.target.value)}
                                            className="text-xs border-2 border-gray-200 rounded py-1 pl-2 pr-6 focus:ring-primary-500 focus:border-primary-500"
                                            disabled={isUpdatingStatus}
                                        >
                                            {AVAILABLE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </select>
                                        <button onClick={handleUpdateStatus} disabled={isUpdatingStatus} className="p-1 px-1.5 text-green-700 hover:bg-green-100 bg-green-50 rounded transition-colors"><Save className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => setEditingStatus(false)} disabled={isUpdatingStatus} className="p-1 px-1.5 text-gray-500 hover:bg-gray-100 bg-gray-50 rounded transition-colors"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                ) : (
                                    <span onClick={() => { setTempStatus(order.status); setEditingStatus(true); }} className={`cursor-pointer inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full hover:ring-2 hover:ring-offset-1 hover:ring-gray-200 transition-all ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                                        {order.status.replace('_', ' ')}
                                        <Edit2 className="w-3 h-3 ml-1.5 opacity-60" />
                                    </span>
                                )}
                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${priorityColors[order.priority] || ''}`}>
                                    {order.priority}
                                </span>
                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${paymentStatusColors[order.paymentStatus] || ''}`}>
                                    {order.paymentStatus}
                                </span>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setShowNotificationModal(true)}
                                        className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors"
                                    >
                                        <Bell className="h-3.5 w-3.5 mr-1.5" /> Notify Customer
                                    </button>
                                    <button
                                        onClick={() => setShowLabel('garment')}
                                        className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        <Printer className="h-3.5 w-3.5 mr-1.5" /> Print Garment Tag
                                    </button>
                                    <button
                                        onClick={() => setShowLabel('receipt')}
                                        className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                        <Printer className="h-3.5 w-3.5 mr-1.5" /> Print Customer Receipt
                                    </button>
                                    <button
                                        onClick={handleEmailReceipt}
                                        disabled={emailSending}
                                        className="inline-flex items-center px-3 py-1.5 bg-sky-600 text-white text-xs font-medium rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50"
                                    >
                                        <Mail className="h-3.5 w-3.5 mr-1.5" /> {emailSending ? 'Sending...' : 'Email Receipt'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left Column — Main Content */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Items Table */}
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <Hash className="w-5 h-5 text-gray-400" /> Order Items
                                        <span className="ml-auto text-sm font-normal text-gray-500">{order.items?.length || 0} items</span>
                                    </h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                                                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Weight</th>
                                                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                                                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">VAT</th>
                                                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                                <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {order.items?.map((item) => (
                                                <tr key={item._id} className="hover:bg-gray-50">
                                                    <td className="px-5 py-3">
                                                        <div className="text-sm font-medium text-gray-900">{item.serviceId?.name || 'Unknown'}</div>
                                                        <div className="text-xs text-gray-500">{item.serviceId?.code}</div>
                                                        {item.isExpress && <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-orange-100 text-orange-700 mt-0.5">EXPRESS</span>}
                                                    </td>
                                                    <td className="px-5 py-3 text-right text-sm text-gray-700">{item.quantity}</td>
                                                    <td className="px-5 py-3 text-right text-sm text-gray-700">{item.weight ? `${item.weight} kg` : '—'}</td>
                                                    <td className="px-5 py-3 text-right text-sm text-gray-700">£{item.unitPrice?.toFixed(2)}</td>
                                                    <td className="px-5 py-3 text-right text-sm text-gray-500">£{item.vatAmount?.toFixed(2)}</td>
                                                    <td className="px-5 py-3 text-right text-sm font-medium text-gray-900">£{item.totalPrice?.toFixed(2)}</td>
                                                    <td className="px-5 py-3 text-center">
                                                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${item.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                            item.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                                                'bg-gray-100 text-gray-700'
                                                            }`}>
                                                            {item.status?.replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!order.items || order.items.length === 0) && (
                                                <tr><td colSpan={7} className="px-5 py-8 text-center text-sm text-gray-400">No items found</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Financial Summary */}
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-gray-400" /> Payment Summary
                                    </h2>
                                </div>
                                <div className="px-5 py-4">
                                    <div className="space-y-2 max-w-sm ml-auto">
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span className="text-gray-700">£{order.subtotal?.toFixed(2)}</span></div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">VAT</span><span className="text-gray-700">£{order.vatAmount?.toFixed(2)}</span></div>
                                        {order.discountAmount > 0 && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-500">Discount{order.appliedDiscount?.code ? ` (${order.appliedDiscount.code})` : ''}</span>
                                                <span className="text-green-600">-£{order.discountAmount?.toFixed(2)}</span>
                                            </div>
                                        )}
                                        {(order.loyaltyPointsEarned || 0) > 0 && (
                                            <div className="flex justify-between text-sm"><span className="text-gray-500">Loyalty Earned</span><span className="text-yellow-600">+{order.loyaltyPointsEarned} pts</span></div>
                                        )}
                                        {(order.loyaltyPointsRedeemed || 0) > 0 && (
                                            <div className="flex justify-between text-sm"><span className="text-gray-500">Loyalty Redeemed</span><span className="text-yellow-600">{order.loyaltyPointsRedeemed} pts</span></div>
                                        )}
                                        <div className="flex justify-between text-sm font-semibold border-t pt-2 mt-2">
                                            <span className="text-gray-900">Total</span><span className="text-gray-900">£{order.totalAmount?.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Paid</span><span className="text-green-600">£{order.paidAmount?.toFixed(2)}</span></div>
                                        {order.balanceAmount > 0 ? (
                                            <>
                                                <div className="flex justify-between text-sm font-medium"><span className="text-red-500">Balance Due</span><span className="text-red-600">£{order.balanceAmount?.toFixed(2)}</span></div>
                                                <button
                                                    onClick={() => setShowPaymentModal(true)}
                                                    className="w-full mt-3 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                                >
                                                    Pay Balance
                                                </button>
                                            </>
                                        ) : (
                                            order.paymentMethod && (
                                                <div className="flex justify-between text-sm"><span className="text-gray-500">Method</span><span className="text-gray-700 capitalize">{order.paymentMethod}</span></div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Notes & Instructions */}
                            {(order.specialInstructions || order.customerNotes || order.internalNotes) && (
                                <div className="bg-white shadow rounded-lg overflow-hidden">
                                    <div className="px-5 py-4 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            <FileText className="w-5 h-5 text-gray-400" /> Notes
                                        </h2>
                                    </div>
                                    <div className="px-5 py-4 space-y-4">
                                        {order.specialInstructions && (
                                            <div>
                                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Special Instructions</p>
                                                <p className="text-sm text-gray-700 bg-yellow-50 rounded-md p-3">{order.specialInstructions}</p>
                                            </div>
                                        )}
                                        {order.customerNotes && (
                                            <div>
                                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Customer Notes</p>
                                                <p className="text-sm text-gray-700">{order.customerNotes}</p>
                                            </div>
                                        )}
                                        {order.internalNotes && (
                                            <div>
                                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Internal Notes</p>
                                                <p className="text-sm text-gray-700 bg-gray-50 rounded-md p-3">{order.internalNotes}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column — Sidebar */}
                        <div className="space-y-6">
                            {/* Customer Card */}
                            {customerId && (
                                <Link to={`/customers/${customerId}`} className="block bg-white shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                                    <div className="px-5 py-4 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            <User className="w-5 h-5 text-gray-400" /> Customer
                                        </h2>
                                    </div>
                                    <div className="px-5 py-4">
                                        {customerData ? (
                                            <>
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                                        <span className="text-primary-600 font-semibold">
                                                            {customerData.firstName?.charAt(0)}{customerData.lastName?.charAt(0)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{customerData.firstName} {customerData.lastName}</p>
                                                        <p className="text-xs text-gray-500">{customerData.email}</p>
                                                    </div>
                                                </div>
                                                {customerData.phone && <p className="text-sm text-gray-600 mb-1">📞 {customerData.phone}</p>}
                                                {(customerData.address || customerData.city || customerData.postcode) && (
                                                    <p className="text-sm text-gray-600 mb-1">📍 {[customerData.address, customerData.city, customerData.postcode].filter(Boolean).join(', ')}</p>
                                                )}
                                            </>
                                        ) : (
                                            <p className="text-sm text-gray-500">Loading customer...</p>
                                        )}
                                        <p className="text-xs text-primary-600 mt-2">View customer details →</p>
                                    </div>
                                </Link>
                            )}

                            {/* Dates & Timeline */}
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-gray-400" /> Timeline
                                    </h2>
                                </div>
                                <div className="px-5 py-4 space-y-3">
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Created</span><span className="text-gray-700">{formatDate(order.createdAt)}</span></div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-gray-500">Due Date</span>
                                        {editingDueDate ? (
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="date"
                                                    value={tempDueDate}
                                                    onChange={(e) => setTempDueDate(e.target.value)}
                                                    className="text-xs border-2 border-gray-200 rounded py-1 px-2 w-[115px] focus:ring-primary-500 focus:border-primary-500"
                                                    disabled={isUpdatingDueDate}
                                                />
                                                <button onClick={handleUpdateDueDate} disabled={isUpdatingDueDate} className="p-1 text-green-700 hover:bg-green-100 bg-green-50 rounded transition-colors"><Save className="w-3.5 h-3.5" /></button>
                                                <button onClick={() => setEditingDueDate(false)} disabled={isUpdatingDueDate} className="p-1 text-gray-500 hover:bg-gray-100 bg-gray-50 rounded transition-colors"><X className="w-3.5 h-3.5" /></button>
                                            </div>
                                        ) : (
                                            <span onClick={() => { setTempDueDate(order.dueDate ? order.dueDate.split('T')[0] : ''); setEditingDueDate(true); }} className="text-gray-700 cursor-pointer hover:text-primary-600 transition-colors inline-flex items-center gap-1.5 font-medium bg-gray-50 hover:bg-primary-50 px-2 py-0.5 rounded-md">
                                                {formatDate(order.dueDate)}
                                                <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Completed</span><span className="text-gray-700">{formatDate(order.completedDate)}</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Collected</span><span className="text-gray-700">{formatDate(order.collectedDate)}</span></div>
                                    {order.createdById && (
                                        <div className="flex justify-between text-sm pt-2 border-t">
                                            <span className="text-gray-500">Created By</span>
                                            <span className="text-gray-700">{order.createdById.firstName} {order.createdById.lastName}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Order Info */}
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <Clock className="w-5 h-5 text-gray-400" /> Details
                                    </h2>
                                </div>
                                <div className="px-5 py-4 space-y-3">
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Type</span><span className="text-gray-700 capitalize">{order.orderType?.replace('_', ' ')}</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-gray-500">Items</span><span className="text-gray-700">{order.itemCount}</span></div>
                                    {order.totalWeight > 0 && (
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Total Weight</span><span className="text-gray-700">{order.totalWeight} kg</span></div>
                                    )}
                                </div>
                            </div>

                            {/* Delivery Info */}
                            {order.isDelivery && (
                                <div className="bg-white shadow rounded-lg overflow-hidden">
                                    <div className="px-5 py-4 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            <Truck className="w-5 h-5 text-gray-400" /> Delivery
                                        </h2>
                                    </div>
                                    <div className="px-5 py-4 space-y-2">
                                        {order.deliveryAddress && <p className="text-sm text-gray-700">{order.deliveryAddress}</p>}
                                        {order.deliveryPostcode && <p className="text-sm text-gray-500">{order.deliveryPostcode}</p>}
                                        {order.deliveryInstructions && (
                                            <p className="text-sm text-gray-600 bg-yellow-50 rounded-md p-2 mt-2">{order.deliveryInstructions}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Label Print Modal */}
            {showLabel && order && (
                <LabelPrint
                    data={{
                        orderNumber: order.orderNumber,
                        customerName: customerData ? `${customerData.firstName} ${customerData.lastName}` : '',
                        customerPhone: customerData?.phone,
                        createdAt: order.createdAt,
                        dueDate: order.dueDate,
                        items: order.items?.map(item => ({
                            name: item.serviceId?.name || 'Unknown Service',
                            code: item.serviceId?.code,
                            quantity: item.quantity,
                            specialInstructions: item.specialInstructions
                        })) || [],
                        subtotal: order.subtotal,
                        vatAmount: order.vatAmount,
                        totalAmount: order.totalAmount,
                        paidAmount: order.paidAmount,
                        discountAmount: order.discountAmount,
                        discountCode: order.appliedDiscount?.code,
                        loyaltyPointsEarned: order.loyaltyPointsEarned,
                        loyaltyPointsRedeemed: order.loyaltyPointsRedeemed,
                        paymentStatus: order.paymentStatus,
                        status: order.status,
                        specialInstructions: order.specialInstructions,
                        siteName: 'Demo Dry Cleaners',
                        sitePhone: '+44 20 7123 4567',
                        siteAddress: '123 Laundry Lane, London, UK',
                        itemCount: order.itemCount || order.items?.length || 0,
                    }}
                    labelType={showLabel}
                    onClose={() => setShowLabel(false)}
                />
            )}
            {/* Payment Modal */}
            {showPaymentModal && order && (
                <PaymentModal
                    orderId={order._id}
                    amount={order.balanceAmount}
                    onClose={() => setShowPaymentModal(false)}
                    onComplete={handlePaymentComplete}
                />
            )}
            {/* Notification Modal */}
            {showNotificationModal && order && (
                <SendNotificationModal
                    orderId={order._id}
                    orderNumber={order.orderNumber}
                    orderStatus={order.status}
                    customerName={customerData ? `${customerData.firstName} ${customerData.lastName}` : 'Customer'}
                    customerEmail={customerData?.email}
                    customerPhone={customerData?.phone}
                    onClose={() => setShowNotificationModal(false)}
                />
            )}
        </>
    );
};

export default OrderDetail;
